import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { SeasonTypeEnum } from '@coin/shared/core/util-enums';
import { Observable } from 'rxjs';
import { environment } from '@coin/shared/core/util-environments';
import { finalize, map, tap } from 'rxjs/operators';
import {
  GenericSeasonSettings,
  MeritSeasonSetting,
  PositionEvaluationSetting,
  SpecialPaymentSetting
} from '@coin/shared/season-mgmt/util';
import { HttpHelpersService, LoadingService } from '@coin/shared/core/data-access';
import { encodeSeasonType } from '@coin/shared/core/util-helpers';

@Injectable({
  providedIn: 'root'
})
export class SeasonSettingsService {
  private httpClient = inject(HttpClient);
  private toast = inject(ToastrService);
  private loadingService = inject(LoadingService);
  private httpHelper = inject(HttpHelpersService);

  public getSeasonSettings(seasonId: string, seasonType: SeasonTypeEnum): Observable<GenericSeasonSettings> {
    return this.httpClient
      .get<GenericSeasonSettings>(
        `${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/settings/seasons/${seasonId}`
      )
      .pipe(
        map(data => (!data ? this.getSeasonSettingsEntity(seasonType, seasonId) : data)),
        this.httpHelper.handleError('Cannot get settings')
      );
  }

  public updateSeasonSettings<T = GenericSeasonSettings>(seasonSettings: T, seasonType: SeasonTypeEnum): Observable<T> {
    this.loadingService.present();
    return this.httpClient
      .put<T>(`${environment.api.baseUrl}/admin/api/v1/${encodeSeasonType(seasonType)}/settings`, seasonSettings)
      .pipe(
        tap(() => this.toast.success('Updated Settings.')),
        this.httpHelper.handleError('Cannot update settings'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public getSeasonSettingsEntity(seasonType: SeasonTypeEnum, seasonId: string): GenericSeasonSettings {
    switch (seasonType) {
      case SeasonTypeEnum.Merit:
        return new MeritSeasonSetting({ seasonId });
      case SeasonTypeEnum.SpecialPayment:
      case SeasonTypeEnum.SpecialPaymentCash:
      case SeasonTypeEnum.SpecialPaymentEquity:
        return new SpecialPaymentSetting({ seasonId });
      case SeasonTypeEnum.PositionEvaluation:
        return new PositionEvaluationSetting({ seasonId });
    }
  }
}
